'use client';

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { usePathname, useSearchParams } from 'next/navigation';

import { config } from '@/config/env';
import { useDashboard, usePage } from '@/modules';

import { usePageGlobalOperations } from '@/modules/global/hooks/usePageGlobalOperations';
import { useDashboardGlobalOperations } from '@/modules/global/hooks/useDashboardGlobalOperations';

import { useNavigate } from '@/hooks/useNavigate';
import { buildExternalUrl } from '@/utils/buildExternalUrl';

import {
  PerformanceDashboardsIcon,
  RollUpDashboardIcon,
  MarketInsightsIcon,
  IncentivesManagerIcon,
  LaunchFormIcon,
  ProcessMonitorIcon,
  VDPConnectIcon,
} from '@/components/icons';

import { SidebarItemGroup } from './SidebarItemGroup';
import { SidebarHeader } from './SidebarHeader';
import { SidebarItem } from './SidebarItem';
import { useAuth } from '@/modules/auth';
import { canViewPage } from '@/modules/pages/utils/permissions';
import { isDisabledByProducts, isDisabledByIncompleteProducts, getPageDisplayTitle, PAGE_BUILDOUT_EXCLUDED_PRODUCTS } from '@/modules/pages/utils/products';


interface SidebarProps {
  isCollapsed: boolean;
  onToggle: (state?: boolean) => void;
  handleNewPage: () => void
}

export const Sidebar = ({ isCollapsed, onToggle, handleNewPage }: SidebarProps) => {
  const navigate = useNavigate();
  const pathname = usePathname();
  const searchParamsHook = useSearchParams();
  const { state: dashboardState } = useDashboard();
  const { state: pageState } = usePage();
  const { user } = useAuth();
  const { selectCurrentPage } = usePageGlobalOperations();
  const [isCreatingPage, setIsCreatingPage] = useState(false);
  const { loadFullDashboard } = useDashboardGlobalOperations();
  const loadedDashboardKey = useRef<string | null>(null);

  const dashboardIdParam = searchParamsHook?.get('dashboardId') || null;
  const pageIdParam = searchParamsHook?.get('pageId') || null;
  const activePageIdFromUrl = pageIdParam ? Number(pageIdParam) : null;

  const isOnSummaryPage = pathname.includes('dashboard/summary') || pageState.currentPage?.title === "Summary";
  const currentPageId = isOnSummaryPage ? -1 : (activePageIdFromUrl || pageState.currentPage?.id || null);


  // * Filter pages by contracted products - only if the metadata.products is set
  const visiblePages = useMemo(() => {
    const filteredByPermissions = pageState.pages.filter((p) => canViewPage(p, user));
    return filteredByPermissions;
  }, [pageState.pages, user]);

  const dealerContractedProducts = useMemo(
    () => (dashboardState?.dealerContractedProducts?.contractedProducts ?? []).map((p) => p.toLowerCase()),
    [dashboardState?.dealerContractedProducts?.contractedProducts]
  );

  const dealerIncompleteProducts = useMemo(
    () => dashboardState?.dealerContractedProducts?.incompleteProducts ?? [],
    [dashboardState?.dealerContractedProducts?.incompleteProducts]
  );

  const isPageDisabled = useCallback(
    (page: (typeof pageState.pages)[number]) =>
      isDisabledByProducts(page?.metadata?.products, dealerContractedProducts) ||
      isDisabledByIncompleteProducts(page?.metadata?.products, dealerIncompleteProducts, PAGE_BUILDOUT_EXCLUDED_PRODUCTS),
    [dealerContractedProducts, dealerIncompleteProducts]
  );

  const getPageLabel = useCallback(
    (page: (typeof pageState.pages)[number]) =>
      getPageDisplayTitle(page?.metadata?.['page-titles'], dealerContractedProducts) ?? page.title,
    [dealerContractedProducts]
  );



  // * Fetch dashboard only when dashboardId / dealer / date / changes
  useEffect(() => {
    const effectiveDashboardId = dashboardIdParam || String(config.DASHBOARD_ID);
    if (!effectiveDashboardId) return;

    if (loadedDashboardKey.current !== effectiveDashboardId || pageState.pages.length === 0) {
      const pageId = pageIdParam ? Number(pageIdParam) : undefined;
      loadFullDashboard(Number(effectiveDashboardId), { defaultPageId: pageId }).then(page => {
        if (page && !pageIdParam) {
          navigate('/dashboard/?pageId=' + page.id)
        }
      });
      loadedDashboardKey.current = `${effectiveDashboardId}-${dashboardState.selectedDealer?.dealerRefId}`;
    }
  }, [dashboardIdParam, dashboardState.selectedDealer]);

  useEffect(() => {
    if (pageState.generatingPage) return;

    if (pageIdParam && visiblePages.length) {
      const page = visiblePages.find(page => page.id === Number(pageIdParam));
      const notFoundPage = !page;
      const isDisabledPage = !!page && isPageDisabled(page);
      const hasPages = visiblePages.length > 0;
      const firstEnabledPage = visiblePages.find((p) => !isPageDisabled(p));
      const firstEnabledPageId = firstEnabledPage?.id;
      // * CASE: URL SPECIFIED PAGE NOT FOUND - USE THE FIRST DASHBOARD PAGE
      if (firstEnabledPageId && (notFoundPage || isDisabledPage) && hasPages) {
        if (pageIdParam !== String(firstEnabledPageId)) {
          navigate(`/dashboard/?pageId=${firstEnabledPageId}`);
          onToggle(true)
        }
        return;
      }
      // * If page is found and is not the current page, select it
      if (page && !isPageDisabled(page) && page.id !== pageState.currentPage?.id) {
        selectCurrentPage(page);
        onToggle(true)
      }
    }
  }, [pageIdParam, visiblePages, currentPageId, pageState.currentPage?.id, pageState.generatingPage, selectCurrentPage, isPageDisabled, navigate, onToggle]);

  const handleExternalLink = (url: string) => {
    if (url) {
      window.open(url, '_blank', 'noopener,noreferrer');
    }
  };

  const incentivesManagerUrl = buildExternalUrl(
    config.INCENTIVES_MANAGER_URL,
    dashboardState.selectedDealer?.dealerRefId,
    user?.userId
  );

  const launchFormUrl = buildExternalUrl(
    config.LAUNCH_FORM_URL,
    dashboardState.selectedDealer?.dealerRefId,
    user?.userId
  );

  const vdpConnectUrl = buildExternalUrl(
    config.VDP_CONNECT_URL,
    dashboardState.selectedDealer?.dealerRefId,
    user?.userId
  );

  const otherSections = [
    {
      icon: <RollUpDashboardIcon />,
      label: 'Roll Up Dashboard',
      showOnlyWithFlag: true,
    },
    {
      icon: <IncentivesManagerIcon />,
      label: 'Incentives Manager',
      isClickable: true,
      onClick: () => handleExternalLink(incentivesManagerUrl),
    },
    {
      icon: <LaunchFormIcon />,
      label: 'Launch Form',
      isClickable: true,
      onClick: () => handleExternalLink(launchFormUrl),
    },
    // {
    //   icon: <MarketInsightsIcon />,
    //   label: 'Market Insights',
    //   badge: 'BETA',
    // },
    {
      icon: <VDPConnectIcon />,
      label: 'VDPConnect™',
      isClickable: true,
      onClick: () => handleExternalLink(vdpConnectUrl),
    },
    {
      icon: <ProcessMonitorIcon />,
      label: 'Process Monitor',
      showOnlyWithFlag: true,
    },
  ].filter(section => !section.showOnlyWithFlag || config.SHOW_NON_UAT_APPS);

  return (
    <>
      <div
        className={`sticky top-0 bg-menu-background border-r border-gray-700 h-screen flex flex-col gap-1 whitespace-nowrap transition-all duration-300 ease-in-out ${isCollapsed ? 'p-4 px-2 w-[76px] items-center' : 'p-4 w-80'
          }`}
      >
        <SidebarHeader
          isCollapsed={isCollapsed}
          onToggle={() => onToggle()}
        />

        {/* Navigation */}
        <div className={`flex-1 space-y-3 overflow-y-auto scrollbar-thin mt-4 transition-[max-width] duration-300 ease-in-out ${isCollapsed ? 'w-full' : 'max-w-[320px]'
          }`}>
          <SidebarItemGroup
            title="Performance Dashboard"
            icon={<PerformanceDashboardsIcon />}
            pages={visiblePages.filter((p) => p.title !== 'Summary')}
            currentPageId={currentPageId}
            onPageClick={(page) => navigate(`/dashboard/?pageId=${page.id}`)}
            onNewPageClick={handleNewPage}
            sidebarCollapsed={isCollapsed}
            isLoading={pageState.loading}
            isCreating={isCreatingPage}
            isPageDisabled={isPageDisabled}
            getPageLabel={getPageLabel}
            onGroupHeaderClick={() => {
              const firstEnabledPage = visiblePages.find((p) => !isPageDisabled(p));
              if (firstEnabledPage) {
                navigate(`/dashboard/?pageId=${firstEnabledPage.id}`);
              }
            }}
            isGroupHeaderActive={isOnSummaryPage}
          />

          {!isCollapsed && pageState.loading && (
            <div className="flex items-center gap-2 px-4 py-2 text-white/60 text-sm animate-fadeIn">
              <div className="animate-spin rounded-full h-4 w-4 border-2 border-white/20 border-t-white/60"></div>
              <span>Loading pages...</span>
            </div>
          )}

          {otherSections.map((section) => (
            <SidebarItem
              key={section.label}
              icon={section.icon}
              label={section.label}
              isCollapsed={isCollapsed}
              isClickable={section.isClickable}
              onClick={section.onClick}
            />
          ))}
        </div>
      </div>
    </>
  );
};
